import React, { useState, useRef, Fragment } from 'react';
import styled from 'styled-components';
import Tab from '@mui/material/Tab';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import { Link } from 'gatsby';
import { MdMenu } from '@react-icons/all-files/md/MdMenu';
import { MdClose } from '@react-icons/all-files/md/MdClose';
import { Text, Box, Flex } from '..';
import { theme } from '../../style';
import { useDetectOutsideClick } from '../../hooks';

const MobileNav = styled(Flex)`
  .mobile-menu {
    .mobile-menu-body {
      position: absolute;
      left: 40px;
      right: 40px;
      top: calc(100% + 8px);
      z-index: 10001;
      opacity: 0;
      visibility: hidden;
      min-height: 450px;
      max-height: 80vh;
      padding: 15px;
      background-color: rgba(255, 255, 255);
      border-radius: 14px;
      box-shadow: rgb(0 0 0 / 10%) 0px 8px 16px, rgb(0 0 0 / 8%) 0px 4px 8px;

      transform: translateY(-10px);
      transition: all 0.2s ease-in-out;

      @supports (backdrop-filter: none) {
        backdrop-filter: blur(16px);
        background-color: rgba(255, 255, 255, 0.8);
      }

      @media ${theme.devices.sm} {
        left: 20px;
        right: 20px;
      }
    }

    &:hover {
      .mobile-menu-body {
        visibility: visible;
        opacity: 1;
        transform: translateY(0);
      }
    }

    &.active {
      > .menu-button {
        background-color: ${theme.colors.grey.v1};

        + {
          .mobile-menu-body {
            visibility: visible;
            opacity: 1;
            transform: translateY(0);
          }
        }
      }
    }
  }

  .button {
    text-transform: none;
    font-size: 16px;
    color: black;
    min-width: 60px;
    margin: 0 2px;
    padding: 8px 4px;
    border-radius: 8px;

    :hover {
      background-color: ${theme.colors.grey.v1};
    }
  }

  .buttonActive {
    color: black !important;
    font-weight: bold;
    background-color: ${theme.colors.grey.v1};
  }

  .menuItem {
    border-radius: 10px;
    min-height: 30px;
  }

  .link {
    text-decoration: none;
    color: ${theme.colors.grey.v6};
  }

  .tabPanel {
    padding: 10px 0;
  }
`;

const ButtonBox = styled(Flex)`
  .custom-button {
    min-width: 40%;
    padding: 4px 18px;
    border-radius: 8px;
    background-color: ${theme.colors.grey.v1};
    color: black;
    text-transform: none;
    font-family: ${theme.fonts.primary};
    font-size: 16px;

    &.dark {
      margin-left: 14px;
      background-color: black;
      color: white;
    }
  }
`;

const MobileNavbar = ({ menu }) => {
  const menuRef = useRef(null);
  const [value, setValue] = useState('0');
  const [isActive, setIsActive] = useDetectOutsideClick(menuRef, false);

  const handleClick = () => {
    setIsActive(!isActive);
  };

  const handleChange = (e, newValue) => {
    setValue(newValue);
  };

  return (
    <MobileNav flexGrow={1} justifyContent="flex-end" ml={12}>
      <Box className={`mobile-menu ${isActive ? 'active' : ''}`}>
        <IconButton onClick={handleClick} className="menu-button">
          {isActive ? <MdClose /> : <MdMenu />}
        </IconButton>
        <Box overflowY="auto" ref={menuRef} className="mobile-menu-body">
          <TabContext value={value}>
            <TabList
              variant="fullWidth"
              onChange={handleChange}
              aria-label="mobile-menu"
              TabIndicatorProps={{
                sx: { backgroundColor: `${theme.colors.grey.v5}` },
              }}
            >
              {Array.isArray(menu) &&
                menu.map((item, index) => (
                  <Tab
                    wrapped
                    label={item.name}
                    value={index.toString()}
                    key={index}
                    className={`button ${
                      value === index.toString() ? 'buttonActive' : ''
                    }`}
                  />
                ))}
            </TabList>

            {Array.isArray(menu) &&
              menu.map((item, index) => (
                <TabPanel
                  value={index.toString()}
                  className="tabPanel"
                  key={index}
                >
                  <Grid container spacing={2}>
                    {Array.isArray(item?.content) &&
                      item.content.map((menuItem, i) => (
                        <Fragment key={i}>
                          {menuItem && (
                            <Grid item xs={6} sm={4} md={3}>
                              {menuItem.category && (
                                <Text color="grey.v7" fontWeight="bold" p={3}>
                                  {menuItem.category}
                                </Text>
                              )}
                              {Array.isArray(menuItem?.items) &&
                                menuItem.items.map((button, key) => (
                                  <MenuItem className="menuItem" key={key}>
                                    <Link to={button.link} className="link">
                                      {button.title}
                                    </Link>
                                  </MenuItem>
                                ))}
                            </Grid>
                          )}
                        </Fragment>
                      ))}
                  </Grid>
                </TabPanel>
              ))}
          </TabContext>

          <ButtonBox justifyContent="center" my={4}>
            <Button href="#" className="custom-button">
              Log in
            </Button>
            <Button href="#" className="custom-button dark">
              Sign up
            </Button>
          </ButtonBox>
        </Box>
      </Box>
    </MobileNav>
  );
};

export default MobileNavbar;
